import http, { request } from './http';
import type { ProjectTeamContext } from './projectApi';

export interface Result<T> {
  success: boolean;
  message?: string;
  data: T;
}

/** 阶段计划交付日期（与后端 PhaseDeliveryDTO 对应） */
export interface PhaseDeliveryDTO {
  phaseCode: string;
  phaseName: string;
  plannedEndTime?: string | null;
}

export interface ProjectSummary {
  id: number;
  name: string;
  /** 项目编号 */
  projectCode?: string | null;
  status: string;
  /** 客户名称 */
  owner?: string | null;
  description?: string;
  /** 项目整体交付日期 */
  projectDeliveryDate?: string | null;
  createdAt?: string;
  creatorUserId?: number | null;
  projectLeadUserId?: number | null;
  /** 需求台账是否已锁定 */
  requirementsLocked?: boolean | null;
  /** NONE | PENDING | APPROVED | REJECTED */
  freezeReviewStatus?: string | null;
  /** 当前语义化版本，如 V1.0.0 */
  semanticVersion?: string | null;
}

export interface RiskSummary {
  total: number;
  highCount: number;
}

export interface DeliverableSummary {
  total: number;
  completedCount: number;
  overdueCount: number;
}

export interface ProjectPhaseInfo {
  id: number;
  projectId: number;
  phaseId: number;
  phaseCode?: string;
  phaseName?: string;
  phaseSortOrder?: number;
  status?: string;
  startTime?: string;
  endTime?: string;
  plannedEndTime?: string;
  healthColor?: string;
  remark?: string;
  deliverableCompletionRate?: number;
  openRiskCount?: number;
  overdueDays?: number;
}

export interface TaskItem {
  id: number;
  projectId: number;
  title: string;
  description?: string;
  status?: string;
  assignee?: string;
  dueDate?: string;
  phaseId?: number;
  phaseName?: string;
  phaseCode?: string;
  autoGenerated?: boolean;
}

export interface ProjectDashboardData {
  project: ProjectSummary;
  currentPhase: ProjectPhaseInfo | null;
  phaseProgressPercent: number;
  taskCompletionPercent: number;
  riskSummary: RiskSummary;
  deliverableSummary: DeliverableSummary;
  recentTodos?: TaskItem[];
  healthScore: number;
  /** 各阶段计划交付日期（概念、计划、开发、验证、发布） */
  phaseDeliveryDates?: PhaseDeliveryDTO[] | null;
}

export interface ProjectPageBootstrap {
  dashboard: ProjectDashboardData;
  teamContext: ProjectTeamContext;
  issueChecklist?: { records?: TaskItem[]; total?: number };
  issueCategories?: string[];
  phaseOpenRisks?: PhaseRiskItem[];
  /** 是否已生成 WBS 快照（轻量字段，避免额外拉取完整 JSON） */
  wbsSnapshotExists?: boolean;
  /** 当前用户是否可管理文档空间分类（项目负责人或创建人） */
  canManageDocCategories?: boolean;
}

export async function getProjectDashboardData(projectId: number): Promise<ProjectDashboardData> {
  const res = await request<Result<ProjectDashboardData>>(
    http.get(`/dashboard/project/${projectId}`),
  );
  return (res as unknown as Result<ProjectDashboardData>).data;
}

/** 项目详情页首屏聚合：单次请求替代多次并行 HTTP */
export async function getProjectPageBootstrap(projectId: number): Promise<ProjectPageBootstrap> {
  const res = await request<Result<ProjectPageBootstrap>>(
    http.get(`/dashboard/project/${projectId}/bootstrap`),
  );
  return (res as unknown as Result<ProjectPageBootstrap>).data;
}

/** 项目下所有阶段状态（用于泳道图） */
export async function getProjectPhases(projectId: number): Promise<ProjectPhaseInfo[]> {
  const res = await request<Result<ProjectPhaseInfo[]>>(
    http.get(`/projects/${projectId}/phases`),
  );
  return (res as unknown as Result<ProjectPhaseInfo[]>).data ?? [];
}

export interface DashboardQueryPayload {
  projectId: number;
  question: string;
}

export interface SuggestedAction {
  title: string;
  description?: string;
  url?: string;
}

export interface LinkItem {
  label: string;
  url: string;
}

/** 自然语言查询返回的报告片段（昨日进展、本周计划等） */
export interface ReportFragmentItem {
  title: string;
  assignee?: string;
  /** 完成时间或截止日期 */
  extra?: string;
}

export interface ReportFragment {
  type: string;
  title: string;
  description?: string;
  items: ReportFragmentItem[];
}

export interface DashboardQueryResult {
  summary: string;
  suggestedActions: SuggestedAction[];
  links: LinkItem[];
  reportFragments?: ReportFragment[];
}

export async function postDashboardQuery(
  payload: DashboardQueryPayload,
): Promise<DashboardQueryResult> {
  const res = await request<Result<DashboardQueryResult>>(
    http.post('/dashboard/query', payload),
  );
  return (res as unknown as Result<DashboardQueryResult>).data;
}

export interface PhaseRiskItem {
  id: number;
  projectPhaseId: number;
  riskDescription: string;
  riskLevel: string;
  status: string;
  mitigation?: string;
}

export interface PhaseDeliverableItem {
  id: number;
  projectPhaseId: number;
  name: string;
  description?: string;
  completionStatus: string;
  attachmentUrl?: string;
  sortOrder: number;
}

export interface PhaseCriterionStatus {
  id: number;
  phaseId: number;
  criteriaType: 'ENTRY' | 'EXIT';
  description: string;
  mandatory: boolean;
  completed: boolean;
}

export async function getPhaseRisks(
  projectId: number,
  phaseId: number,
): Promise<PhaseRiskItem[]> {
  const res = await request<Result<PhaseRiskItem[]>>(
    http.get(`/projects/${projectId}/phases/${phaseId}/risks`),
  );
  return (res as unknown as Result<PhaseRiskItem[]>).data ?? [];
}

export async function getPhaseDeliverables(
  projectId: number,
  phaseId: number,
): Promise<PhaseDeliverableItem[]> {
  const res = await request<Result<PhaseDeliverableItem[]>>(
    http.get(`/projects/${projectId}/phases/${phaseId}/deliverables`),
  );
  return (res as unknown as Result<PhaseDeliverableItem[]>).data ?? [];
}

/** 项目阶段准入/准出规则及完成情况 */
export async function getPhaseCriteria(
  projectId: number,
  phaseId: number,
): Promise<PhaseCriterionStatus[]> {
  const res = await request<Result<PhaseCriterionStatus[]>>(
    http.get(`/projects/${projectId}/phases/${phaseId}/criteria`),
  );
  return (res as unknown as Result<PhaseCriterionStatus[]>).data ?? [];
}
